import { navigate } from 'gatsby'
import { useCallback, useEffect, useMemo } from 'react'
import { useConfigParams } from '../../config'
import { useLocation } from '../../pages'
import { useSiteMetadata } from '../../sanity/hooks/useSiteMetadata'
import { RegistrationType } from '../types'

export interface UseRegistrationTypes {
  isGuestCheckoutEnabled: boolean
  allowPractitioner: boolean
  allowPLPractitioner: boolean
  allowPLUnlicensed: boolean
  allowPatient: boolean
  allowPatientZipCode: boolean
  allowStudent: boolean
  isSelectionAllowed: boolean
  isValidAllowedType: (type: string) => type is RegistrationType
  selectedType: RegistrationType | null
  selectType: (type: RegistrationType) => void
}

export const useRegistrationTypes = (): UseRegistrationTypes => {
  const { practitionerCode } = useConfigParams()

  const isGuestCheckoutEnabled = 'STEVENMZODKOY' === (practitionerCode || '').toUpperCase()

  const { featureFlags } = useSiteMetadata() || {}

  const { pathname, search, hash } = useLocation()
  const locationHash = hash.replace('#', '') || null
  const isDispensary = !!practitionerCode

  const allowedTypes = useMemo<Record<RegistrationType, boolean>>(
    () => ({
      practitioner: (featureFlags?.allowPractitionerRegistration ?? false) && !isDispensary,
      'private-label-practitioner':
        (featureFlags?.allowPrivateLabelRegistration ?? false) && !isDispensary,
      'private-label-unlicensed':
        (featureFlags?.allowPrivateLabelRegistration ?? false) && !isDispensary,
      patient:
        (featureFlags?.allowPatientRegistration ||
          (featureFlags?.allowDispensaryPatientRegistration && isDispensary)) ??
        false,
      'patient-zip-code': (featureFlags?.allowPatientZipCodeRegistration ?? false) && !isDispensary,
      student: (featureFlags?.allowStudentRegistration ?? false) && !isDispensary,
    }),
    [featureFlags, isDispensary]
  )

  const isValidAllowedType = useCallback(
    (type: string): type is RegistrationType =>
      (type && allowedTypes[type as RegistrationType]) || false,
    [allowedTypes]
  )

  const singleAllowedType = useMemo(() => {
    // intentionally omit 'patient-zip-code' for singling out type
    const allowedTypeNames = [
      'practitioner',
      'private-label-practitioner',
      'private-label-unlicensed',
      'patient',
      'student',
    ].filter(isValidAllowedType)
    if (allowedTypeNames.length === 1) {
      return allowedTypeNames[0]
    }
    return null
  }, [isValidAllowedType])

  const selectedType = useMemo(() => {
    if (singleAllowedType) {
      return singleAllowedType
    }
    if (!locationHash || !isValidAllowedType(locationHash)) {
      return null
    }
    return locationHash
  }, [singleAllowedType, locationHash, isValidAllowedType])

  const selectType = useCallback(
    (type: RegistrationType) => {
      if (!isValidAllowedType(type)) {
        return
      }
      navigate(`${pathname}${search}#${type}`)
    },
    [pathname, search, isValidAllowedType]
  )

  useEffect(() => {
    if (!selectedType && locationHash) {
      navigate(`${pathname}${search}`, { replace: true })
    }
  }, [locationHash, pathname, search, selectedType])

  return {
    isGuestCheckoutEnabled,
    allowPractitioner: allowedTypes.practitioner,
    allowPLPractitioner: allowedTypes['private-label-practitioner'],
    allowPLUnlicensed: allowedTypes['private-label-unlicensed'],
    allowPatient: allowedTypes.patient,
    allowPatientZipCode: allowedTypes['patient-zip-code'],
    allowStudent: allowedTypes.student,
    isSelectionAllowed: !singleAllowedType,
    isValidAllowedType,
    selectedType,
    selectType,
  }
}
